import { inject, Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { DatiqGraphqlApolloService } from '@dat/datiq-graphql';
import { gql } from 'apollo-angular';
import { firstValueFrom } from 'rxjs';

import {
    CapacityLevels,
    Direction,
    EquipmentType,
    GeoType,
    GeoTypes,
    McLocation,
    McLocationGeoCode,
    McLtTimeline,
    McMciTimeline,
    McQuery,
    McResponseLt,
    McResponseLtSchema,
    McResponseMci,
    McResponseMciSchema,
    TimeFrame,
} from './iq-mc-event-bus-state.models';
import { DateTime } from 'luxon';
import { isNull } from 'lodash';

type LocationCode = {
    code: string;
    type: string;
    name: string;
};

export type LocationRes = {
    id: string;
    city: string;
    county: string;
    displayName: string;
    latitude: number;
    longitude: number;
    placeId: number;
    postalCode: string;
    stateProv: string;
    geoCodes: LocationCode[];
    error: string;
    hasError: boolean;
};

type LtResponseTimeline = {
    date: string;
    code: string;
    name: string;
    loads: number;
    trucks: number;
    ratio: number;
    type: string;
    equipmentType: string;
    error: string;
    hasError: boolean;
};

type LtResponse = {
    date: string;
    code: string;
    name: string;
    loads: number;
    trucks: number;
    ratio: number;
    type: string;
    equipmentType: string;
    error: string;
    hasError: boolean;
    timeline: LtResponseTimeline[];
};

type MciResponseTimeline = {
    date: string;
    code: string;
    name: string;
    mci: number;
    predictedIntervalUpperBound: number;
    predictedIntervalLowerBound: number;
    capacity: string;
    calulationDate: string;
    type: string;
    equipmentType: string;
    error: string;
    hasError: boolean;
};

type MciResponseCapacity = {
    high: number;
    low: number;
    color: string;
    name: string;
    index: number;
};

type MciResponse = {
    timeline: MciResponseTimeline[];
    date: string;
    code: string;
    name: string;
    mci: number;
    predictedIntervalUpperBound: number;
    predictedIntervalLowerBound: number;
    timeframe: TimeFrame;
    calculationDate: string;
    type: GeoType;
    equipmentType: EquipmentType;
    error: string;
    hasError: boolean;
    capacity: string;
    capacityColor: string;
    capacities: MciResponseCapacity[];
};

@Injectable({
    providedIn: 'root',
})
export class IqMcEventBusPluginGraphqlService {
    private readonly _apollo = inject(DatiqGraphqlApolloService);
    constructor() {}

    async getMci(query: McQuery): Promise<McResponseMci[]> {
        const { data }: ApolloQueryResult<{ mci: MciResponse[] }> =
            await firstValueFrom(
                this._apollo.getApollo().query<{ mci: MciResponse[] }>({
                    query: getMciQuery(),
                    variables: {
                        query: {
                            equipmentType: query.equipmentType,
                            areaType: query.geoType,
                            timeframe: query.timeframe,
                            geoCode: query.geoCode,
                            search: query.displayName,
                            direction: query.direction,
                        },
                    },
                }),
            );

        if (data?.mci[0]?.hasError) {
            return [];
        }
        return data.mci.map((mci: MciResponse): McResponseMci => {
            const check: Partial<McResponseMci> = {
                loaded: true,
                loadedInMs: Date.now(),
                code: mci.code,
                name: mci.name,
                type: mci.type as GeoType,
                timeframe: mci.timeframe as TimeFrame,
                equipmentType: mci.equipmentType as EquipmentType,
                capacity: mci.capacity as CapacityLevels,
                capacityColor: mci.capacityColor,
                mci: mci.mci,
                predictedIntervalUpperBound: mci.predictedIntervalUpperBound,
                predictedIntervalLowerBound: mci.predictedIntervalLowerBound,
                date: mci.date,
                error: mci.error,
                direction: query.direction as Direction,
                hasError: mci.hasError,
                capacityMap: mci.capacities.reduce((acc, cap) => {
                    return {
                        ...acc,
                        [cap.name]: {
                            high: cap.high,
                            low: cap.low,
                            color: cap.color,
                            index: cap.index,
                        },
                    };
                }, {}),
            };

            return McResponseMciSchema.parse(check);
        });
    }

    async getMciTimeline(query: McQuery): Promise<McResponseMci[]> {
        const {data} : ApolloQueryResult<{mci: Partial<MciResponse[]>}> = await firstValueFrom(
            this._apollo.getApollo().query<{mci: Partial<MciResponse[]>}>({
                query: getMciTimelineQuery(),
                variables: {
                    query: {
                        equipmentType: query.equipmentType,
                        areaType: query.geoType,
                        timeframe: query.timeframe,
                        geoCode: query.geoCode,
                        search: query.displayName,
                        direction: query.direction,
                    },
                }
            })
        );

        return data.mci.map((mci: MciResponse): McResponseMci => {
            const mappedTimeline: Record<string, McMciTimeline> = !!mci.timeline?.length ? this.mapMciTimeline(mci.timeline) : {};
            const check: Partial<McResponseMci> = {
                loaded: true,
                loadedInMs: Date.now(),
                code: mci.code,
                name: mci.name,
                type: mci.type as GeoTypes,
                timeframe: mci.timeframe as TimeFrame,
                equipmentType: mci.equipmentType as EquipmentType,
                date: mci.date,
                error: mci.error,
                direction: query.direction as Direction,
                hasError: mci.hasError,
                timeline: mappedTimeline,
            };
            return McResponseMciSchema.parse(check);
        });
    }

    async getLt(query: McQuery): Promise<McResponseLt[]> {
        const { data }: ApolloQueryResult<{ lt: LtResponse[] }> =
            await firstValueFrom(
                this._apollo.getApollo().query<{ lt: any }>({
                    query: getLtQuery(),
                    variables: {
                        query: {
                            equipmentType: query.equipmentType,
                            areaType: query.geoType,
                            timeframe: query.timeframe,
                            geoCode: query.geoCode ?? '',
                            search: query.displayName,
                        },
                    },
                }),
            );
        return data.lt.map((lt): McResponseLt => {
            const mappedTimeline: Record<string, McLtTimeline> = !!lt.timeline
                ?.length
                ? this.mapLtTimeline(lt.timeline)
                : {};
            const error = lt.error
                ? lt.error
                : [lt.loads, lt.ratio, lt.trucks].some(isNull)
                ? 'No Data'
                : '';
            const check: Partial<McResponseLt> = {
                loads: lt.loads ?? 0,
                trucks: lt.trucks ?? 0,
                ratio: lt.ratio,
                date: lt.date ?? '',
                error,
                hasError: lt.hasError,
                equipmentType: query.equipmentType,
                code: lt.code,
                name: lt.name ?? '',
                type: lt.type as GeoType,
                timeline: mappedTimeline,
                loaded: true,
                loadedInMs: Date.now(),
                timeframe: query.timeframe,
            };
            return McResponseLtSchema.parse(check);
        });
    }

    async getLtTimeline(query: McQuery): Promise<McResponseLt[]> {
        const { data }: ApolloQueryResult<{ lt: Partial<LtResponse[]> }> =
            await firstValueFrom(
                this._apollo.getApollo().query<{ lt: Partial<LtResponse[]> }>({
                    query: getLtTimelineQuery(),
                    variables: {
                        query: {
                            equipmentType: query.equipmentType,
                            areaType: query.geoType,
                            timeframe: query.timeframe,
                            geoCode: query.geoCode ?? '',
                            search: query.displayName,
                        },
                    },
                }),
            );
        if (data?.lt[0]?.hasError) {
            return [];
        }
        return data.lt.map((lt): McResponseLt => {
            const mappedTimeline: Record<string, McLtTimeline> = !!lt.timeline
                ?.length
                ? this.mapLtTimeline(lt.timeline)
                : {};
            const error = lt.error
                ? lt.error
                : [lt.loads, lt.ratio, lt.trucks].some(isNull)
                ? 'No Data'
                : '';
            const check: Partial<McResponseLt> = {
                code: lt.code,
                equipmentType: query.equipmentType,
                type: query.geoType,
                timeline: mappedTimeline,
                loaded: true,
                loadedInMs: Date.now(),
                timeframe: query.timeframe,
            };
            return McResponseLtSchema.parse(check);
        });
    }

    mapLtTimeline(timeline: LtResponseTimeline[]) {
        return timeline.reduce(
            (
                timelines: Record<string, McLtTimeline>,
                lt: LtResponseTimeline,
            ): Record<string, McLtTimeline> => {
                const key = this.createTimelineKey(lt.date);
                const timelineNode: McLtTimeline = {
                    date: key,
                    code: lt.code,
                    name: lt.name,
                    loads: lt.loads ?? 0,
                    trucks: lt.trucks ?? 0,
                    ratio: lt.ratio ?? 0,
                    type: lt.type as GeoType,
                    equipmentType: lt.equipmentType as EquipmentType,
                    error: lt.error,
                    hasError: lt.hasError,
                    loaded: true,
                    loadedInMs: Date.now(),
                };
                return {
                    ...timelines,
                    [key]: timelineNode,
                };
            },
            {},
        );
    }

    mapMciTimeline(timeline: MciResponseTimeline[]) {
        return timeline.reduce(
            (
                timelines: Record<string, McMciTimeline>,
                mci: MciResponseTimeline,
            ): Record<string, McMciTimeline> => {
                const key = this.createTimelineKey(mci.date);
                const timelineNode: McMciTimeline = {
                    date: key,
                    code: mci.code,
                    name: mci?.name ?? '',
                    mci: mci.mci,
                    predictedIntervalUpperBound: mci.predictedIntervalUpperBound,
                    predictedIntervalLowerBound: mci.predictedIntervalLowerBound,
                    capacity: mci.capacity as CapacityLevels,
                    calculationDate: mci.calulationDate,
                    type: mci.type as GeoTypes,
                    equipmentType: mci.equipmentType as EquipmentType,
                };
                return {
                    ...timelines,
                    [key]: timelineNode,
                };
            },
            {},
        );
    }    

    createTimelineKey(dateLikeKey: string): string {
        return DateTime.fromJSDate(new Date(dateLikeKey)).toFormat(
            'MM-dd-yyyy',
        );
    }

    async getLocation({ search }: { search: string }): Promise<McLocation> {
        const { data }: ApolloQueryResult<{ location: LocationRes }> =
            await firstValueFrom(
                this._apollo.getApollo().query<{ location: LocationRes }>({
                    query: getLocationQuery(),
                    variables: {
                        query: {
                            search: search,
                        },
                    },
                }),
            );
        const geoCodes: McLocationGeoCode = {
            MKT: this.getGeoCode(GeoTypes.Market, data),
            X_MKT: this.getGeoCode(GeoTypes.ExtendedMarket, data),
            COUNTRY: this.getGeoCode(GeoTypes.Country, data),
            REGION: this.getGeoCode(GeoTypes.Region, data),
            STATE: [
                {
                    code: data.location.stateProv,
                    type: GeoTypes.State,
                    name: data.location.stateProv,
                },
            ],
            THREEDZ: this.getGeoCode(GeoTypes.ThreeDZ, data),
        };

        return {
            id: data.location.id,
            city: data.location.city,
            county: data.location.county,
            displayName: data.location.displayName,
            latitude: data.location.latitude,
            longitude: data.location.longitude,
            placeId: data.location.placeId,
            postalCode: data.location.postalCode,
            stateProv: data.location.stateProv,
            error: data.location.error,
            hasError: data.location.hasError,

            geoCodes,
        };
    }

    private getGeoCode(
        type: GeoType,
        data: { location: LocationRes },
    ): {
        code?: string;
        type?: GeoType;
        name?: string;
    }[] {
        const codeList = data.location.geoCodes.filter(
            (code) => code.type === type,
        );

        return codeList.map((code) => ({
            code: code?.code,
            type: code?.type as GeoType,
            name: code?.name,
        }));
    }
}
function getLtQuery() {
    return gql`
        query ($query: PocLtQuery!) {
            lt: PocLt(query: $query) {
                loads
                trucks
                ratio
                date
                error
                hasError
                code
                name
                type
                timeline(query: $query) {
                    date
                    loads
                    trucks
                    code
                    name
                    type
                    ratio
                }
            }
        }
    `;
}

function getLtTimelineQuery() {
    return gql`
        query ($query: PocLtQuery!) {
            lt: PocLt(query: $query) {
                error
                hasError
                code
                timeline(query: $query) {
                    date
                    loads
                    trucks
                    code
                    name
                    type
                    ratio
                }
            }
        }
    `
}

function getMciQuery() {
    return gql`
        query ($query: PocMciQuery!) {
            mci: PocMci(query: $query) {
                capacity
                mci
                timeframe
                code
                name
                direction
                type
                equipmentType
                predictedIntervalLowerBound
                predictedIntervalUpperBound
                date
                calculationDate
                error
                hasError
                capacityColor
                capacities {
                    name
                    high
                    low
                    color
                    index
                }
            }
        }
    `;
}

function getMciTimelineQuery() {
    return gql`
        query ($query: PocMciQuery!) {
            mci: PocMci(query: $query) {
                code
                name
                error
                hasError
                type
                timeframe
                equipmentType
                calculationDate
                date
                timeline(query: $query){
                    date
                    code
                    name
                    mci
                    predictedIntervalUpperBound
                    predictedIntervalLowerBound
                    capacity
                    timeframe
                    calculationDate
                    type
                    equipmentType
                    error
                    hasError
                }
            }
        }
    `;
}

function getLocationQuery() {
    return gql`
        query ($query: PocGeoLocationQuery!) {
            location: PocGeoLocation(query: $query) {
                id
                city
                county
                displayName
                latitude
                longitude
                placeId
                postalCode
                stateProv
                geoCodes {
                    code
                    type
                    name
                }
                error
                hasError
            }
        }
    `;
}
